import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import leadsApi from "../api/leadsApi";
import campaignApi from "../api/campaignApi";
import { useAuthStore } from "../store/useAuthStore";
import "./WonLeads.css";

/* =========================================================
   CONTACT NAME
   ========================================================= */
const getContactName = (lead) => {
  const fullName = `${lead?.firstName || ""} ${lead?.lastName || ""}`
    .replace(/\s+/g, " ")
    .trim();

  return fullName || lead?.contact || "-";
};

/* =========================================================
   FORMAT CURRENCY
   ========================================================= */
const formatCurrency = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount) || amount <= 0) {
    return "-";
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

/* =========================================================
   FORMAT DATE
   ========================================================= */
const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* =========================================================
   GET COMPANY INITIALS
   ========================================================= */
const getInitials = (value) => {
  return (
    String(value || "?")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase())
      .join("") || "?"
  );
};

/* =========================================================
   FILTER OPTIONS
   ========================================================= */
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const QUARTERS = [
  {
    key: "q1",
    months: [0, 1, 2],
  },
  {
    key: "q2",
    months: [3, 4, 5],
  },
  {
    key: "q3",
    months: [6, 7, 8],
  },
  {
    key: "q4",
    months: [9, 10, 11],
  },
];

/* =========================================================
   QUARTER LABEL
   ========================================================= */
const getQuarterLabel = (quarter, year) => {
  const startMonth = MONTH_NAMES[quarter.months[0]];

  const endMonth = MONTH_NAMES[quarter.months[2]];

  const shortYear = String(year).slice(-2);

  return `${startMonth}-${endMonth}-${shortYear}`;
};

/* =========================================================
   CURRENT WEEK RANGE
   Monday to Sunday
   ========================================================= */
const getCurrentWeekRange = () => {
  const now = new Date();

  const start = new Date(now);
  start.setHours(0, 0, 0, 0);

  const day = start.getDay();

  const diffToMonday = day === 0 ? -6 : 1 - day;

  start.setDate(start.getDate() + diffToMonday);

  const end = new Date(start);

  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return {
    start,
    end,
  };
};

/* =========================================================
   AVAILABLE YEARS
   ========================================================= */
const getAvailableYears = (leads = []) => {
  const years = new Set([new Date().getFullYear()]);

  leads.forEach((lead) => {
    if (!lead?.wonAt) {
      return;
    }

    const date = new Date(lead.wonAt);

    if (!Number.isNaN(date.getTime())) {
      years.add(date.getFullYear());
    }
  });

  return [...years].sort((first, second) => second - first);
};

/* =========================================================
   CHECK DATE FILTER
   ========================================================= */
const isWithinFilter = (wonAt, { year, month, quarter, weekly }) => {
  if (weekly) {
    if (!wonAt) {
      return false;
    }

    const date = new Date(wonAt);

    if (Number.isNaN(date.getTime())) {
      return false;
    }

    const { start, end } = getCurrentWeekRange();

    return date >= start && date <= end;
  }

  if (year === "all" && month === "all" && quarter === "all") {
    return true;
  }

  if (!wonAt) {
    return false;
  }

  const date = new Date(wonAt);

  if (Number.isNaN(date.getTime())) {
    return false;
  }

  if (year !== "all" && date.getFullYear() !== Number(year)) {
    return false;
  }

  if (month !== "all") {
    return date.getMonth() === Number(month);
  }

  if (quarter !== "all") {
    const selectedQuarter = QUARTERS.find((item) => item.key === quarter);

    if (selectedQuarter && !selectedQuarter.months.includes(date.getMonth())) {
      return false;
    }
  }

  return true;
};

/* =========================================================
   WON LEADS PAGE
   ========================================================= */
const WonLeads = () => {
  const navigate = useNavigate();

  const { authUser } = useAuthStore();

  /* =======================================================
     ROLE CHECK

     Only Admin can see amount-related information.
     ======================================================= */
  const currentRole = String(authUser?.role || "")
    .trim()
    .toLowerCase();

  const isAdmin = currentRole === "admin";

  /*
    Admin table columns:
    Company, Campaign, Contact, Email, Phone,
    Proposal Value, Won Date, Actions = 8

    User table columns:
    Company, Campaign, Contact, Email, Phone,
    Won Date, Actions = 7
  */
  const tableColumnCount = isAdmin ? 8 : 7;

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [leads, setLeads] = useState([]);

  const [search, setSearch] = useState("");

  const [filterYear, setFilterYear] = useState("all");

  const [filterMonth, setFilterMonth] = useState("all");

  const [filterQuarter, setFilterQuarter] = useState("all");

  const [filterWeekly, setFilterWeekly] = useState(false);

  const [filterCampaign, setFilterCampaign] = useState("all");

  const [campaigns, setCampaigns] = useState([]);

  /* =======================================================
     LOAD WON LEADS
     ======================================================= */
  const loadWonLeads = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [data, campaignResponse] = await Promise.all([
        leadsApi.getAll(),
        campaignApi.getAll(),
      ]);

      const allLeads = Array.isArray(data)
        ? data
        : Array.isArray(data?.leads)
          ? data.leads
          : Array.isArray(data?.data)
            ? data.data
            : [];

      const campaignList =
        campaignResponse?.campaigns ||
        campaignResponse?.data?.campaigns ||
        campaignResponse?.data ||
        campaignResponse ||
        [];

      setCampaigns(Array.isArray(campaignList) ? campaignList : []);

      const wonLeads = allLeads.filter((lead) => {
        return (
          String(lead?.status || "")
            .trim()
            .toLowerCase() === "won"
        );
      });

      setLeads(wonLeads);
    } catch (err) {
      console.error("[load-won-leads-error]", err);

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load won leads.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWonLeads();
  }, [loadWonLeads]);

  /* =======================================================
     AVAILABLE YEAR OPTIONS
     ======================================================= */
  const availableYears = useMemo(() => {
    return getAvailableYears(leads);
  }, [leads]);

  /* =======================================================
     AVAILABLE CAMPAIGN OPTIONS
     ======================================================= */
  const availableCampaigns = useMemo(() => {
    const masterNames = campaigns
      .filter((item) => item?.isActive !== false)
      .map((item) => String(item?.name || "").trim())
      .filter(Boolean);

    const wonLeadCampaigns = leads
      .map((lead) => String(lead?.campaign || "").trim())
      .filter(Boolean);

    return [...new Set([...masterNames, ...wonLeadCampaigns])].sort((a, b) =>
      a.localeCompare(b),
    );
  }, [campaigns, leads]);

  /* =======================================================
     DATE FILTER FUNCTION
     ======================================================= */
  const matchesDateFilter = useCallback(
    (wonAt) => {
      return isWithinFilter(wonAt, {
        year: filterYear,
        month: filterMonth,
        quarter: filterQuarter,
        weekly: filterWeekly,
      });
    },
    [filterYear, filterMonth, filterQuarter, filterWeekly],
  );

  /* =======================================================
     SEARCH AND FILTERED LEADS
     ======================================================= */
  const filtered = useMemo(() => {
    const searchText = String(search || "")
      .trim()
      .toLowerCase();

    return leads
      .filter((lead) => matchesDateFilter(lead?.wonAt))
      .filter((lead) => {
        if (filterCampaign === "all") {
          return true;
        }

        return String(lead?.campaign || "").trim() === filterCampaign;
      })
      .filter((lead) => {
        if (!searchText) {
          return true;
        }

        const searchValues = [
          lead?.company,
          getContactName(lead),
          lead?.email,
          lead?.contactNo,
          lead?.campaign,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchValues.includes(searchText);
      });
  }, [leads, search, matchesDateFilter, filterCampaign]);

  /* =======================================================
     CHECK WHETHER ANY FILTER IS ACTIVE
     ======================================================= */
  const isFiltered =
    Boolean(search.trim()) ||
    filterWeekly ||
    filterYear !== "all" ||
    filterMonth !== "all" ||
    filterQuarter !== "all" ||
    filterCampaign !== "all";

  /* =======================================================
     ADMIN-ONLY AMOUNT CALCULATIONS
     ======================================================= */
  const totalValue = useMemo(() => {
    if (!isAdmin) {
      return 0;
    }

    return filtered.reduce((sum, lead) => {
      const amount = Number(lead?.proposalValue || lead?.estValue || 0);

      return sum + (Number.isFinite(amount) ? amount : 0);
    }, 0);
  }, [filtered, isAdmin]);

  const avgDealSize =
    isAdmin && filtered.length ? totalValue / filtered.length : 0;

  /* =======================================================
     UNIQUE COMPANIES
     ======================================================= */
  const uniqueCompanies = useMemo(() => {
    return new Set(
      filtered
        .map((lead) => String(lead?.company || "").trim())
        .filter(Boolean),
    ).size;
  }, [filtered]);

  /* =======================================================
     CLEAR ALL FILTERS
     ======================================================= */
  const clearFilters = () => {
    setSearch("");
    setFilterWeekly(false);
    setFilterYear("all");
    setFilterMonth("all");
    setFilterQuarter("all");
    setFilterCampaign("all");
  };

  return (
    <div className="won-page">
      {/* Page Header */}
      <div className="won-header">
        <div>
          <p className="won-eyebrow">CRM / Sales</p>

          <h2>🏆 Won Leads</h2>

          <p className="won-subtitle">
            {loading
              ? "Loading…"
              : `Total Won Leads : ${filtered.length}${
                  isFiltered ? ` of ${leads.length}` : ""
                }`}
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div
        className={["won-kpi-grid", !isAdmin ? "won-kpi-grid-user" : ""]
          .filter(Boolean)
          .join(" ")}
      >
        <KpiCard tone="green" label="Total Won" value={filtered.length} />

        {/* Admin-only amount cards */}
        {isAdmin && (
          <>
            <KpiCard
              tone="blue"
              label="Total Value"
              value={formatCurrency(totalValue)}
            />

            <KpiCard
              tone="amber"
              label="Avg Deal Size"
              value={formatCurrency(avgDealSize)}
            />
          </>
        )}

        <KpiCard tone="purple" label="Companies" value={uniqueCompanies} />
      </div>

      {/* Filters */}
      <div className="won-toolbar">
        <div className="won-search-box">
          <span className="won-search-icon">🔍</span>

          <input
            type="text"
            placeholder="Search company, contact or email…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <button
          type="button"
          className={
            filterWeekly
              ? "won-date-filter-btn won-date-filter-btn-active"
              : "won-date-filter-btn"
          }
          onClick={() => {
            setFilterWeekly((previousValue) => {
              const nextValue = !previousValue;

              if (nextValue) {
                setFilterYear("all");
                setFilterMonth("all");
                setFilterQuarter("all");
              }

              return nextValue;
            });
          }}
        >
          This Week
        </button>

        <select
          className="won-date-filter"
          value={filterYear}
          disabled={filterWeekly}
          onChange={(event) => {
            setFilterWeekly(false);

            setFilterYear(event.target.value);
          }}
        >
          <option value="all">All Years</option>

          {availableYears.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>

        <select
          className="won-date-filter"
          value={filterMonth}
          disabled={filterWeekly}
          onChange={(event) => {
            setFilterWeekly(false);

            setFilterMonth(event.target.value);

            setFilterQuarter("all");
          }}
        >
          <option value="all">All Months</option>

          {MONTH_NAMES.map((monthName, index) => (
            <option key={monthName} value={index}>
              {monthName}
            </option>
          ))}
        </select>

        <select
          className="won-date-filter"
          value={filterQuarter}
          disabled={filterWeekly}
          onChange={(event) => {
            setFilterWeekly(false);

            setFilterQuarter(event.target.value);

            setFilterMonth("all");
          }}
        >
          <option value="all">All Quarters</option>

          {QUARTERS.map((quarter) => (
            <option key={quarter.key} value={quarter.key}>
              {getQuarterLabel(
                quarter,
                filterYear === "all" ? new Date().getFullYear() : filterYear,
              )}
            </option>
          ))}
        </select>

        <select
          className="won-date-filter"
          value={filterCampaign}
          onChange={(event) => setFilterCampaign(event.target.value)}
          title="Filter won leads by campaign"
        >
          <option value="all">All Campaigns</option>

          {availableCampaigns.map((campaign) => (
            <option key={campaign} value={campaign}>
              {campaign}
            </option>
          ))}
        </select>

        {isFiltered && (
          <button
            type="button"
            className="won-clear-filter-btn"
            onClick={clearFilters}
          >
            Clear
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="won-error">
          <span>{error}</span>

          <button type="button" onClick={loadWonLeads}>
            Try Again
          </button>
        </div>
      )}

      {/* Won Leads Table */}
      <div className="won-table-card">
        <div className="won-table-scroll">
          <table className="won-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Campaign</th>
                <th>Contact</th>
                <th>Email</th>
                <th>Phone</th>

                {/* Admin-only amount column */}
                {isAdmin && <th>Proposal Value</th>}

                <th>Won Date</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={tableColumnCount} className="won-state-cell">
                    Loading won leads…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={tableColumnCount} className="won-state-cell">
                    <div className="won-empty">
                      <div className="won-empty-icon">🏆</div>

                      <strong>No won leads found</strong>

                      <span>
                        {isFiltered
                          ? "No won leads match your filters."
                          : "Leads marked as Won will show up here."}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((lead) => (
                  <tr
                    key={lead._id}
                    onClick={() => navigate(`/leads/${lead._id}`)}
                    style={{ cursor: "pointer" }}
                    title="Click to view lead details"
                  >
                    {/* Company */}
                    <td>
                      <div className="won-company-cell">
                        <span className="won-company-avatar">
                          {getInitials(lead?.company)}
                        </span>

                        <strong>{lead?.company || "Unnamed Company"}</strong>
                      </div>
                    </td>

                    {/* Campaign */}
                    <td>{lead?.campaign || "-"}</td>

                    {/* Contact */}
                    <td>{getContactName(lead)}</td>

                    {/* Email */}
                    <td>
                      {lead?.email ? (
                        <a
                          href={`mailto:${lead.email}`}
                          className="won-email-link"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {lead.email}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>

                    {/* Phone */}
                    <td>{lead?.contactNo || "-"}</td>

                    {/* Admin-only proposal value */}
                    {isAdmin && (
                      <td className="won-value-cell">
                        {formatCurrency(lead?.proposalValue || lead?.estValue)}
                      </td>
                    )}

                    {/* Won Date */}
                    <td>{formatDate(lead?.wonAt)}</td>

                    {/* Action */}
                    <td onClick={(event) => event.stopPropagation()}>
                      <div className="won-actions">
                        <span className="badge">WON</span>

                        <button
                          type="button"
                          className="won-action-btn"
                          onClick={() => navigate(`/leads/${lead._id}`)}
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* =========================================================
   KPI CARD COMPONENT
   ========================================================= */
const KpiCard = ({ tone, label, value }) => {
  return (
    <div className={`won-kpi-card won-kpi-${tone}`}>
      <span className="won-kpi-label">{label}</span>

      <strong className="won-kpi-value">{value}</strong>
    </div>
  );
};

export default WonLeads;
